import { useState } from 'react'
import type { User } from '@supabase/supabase-js'
import { CrownedBirdLogo } from './CrownedBirdLogo'
import { UserMenu } from './UserMenu'

export type AdminTab = 'dashboard' | 'billing' | 'users' | 'research' | 'email-logs' | 'email-testing' | 'ops'

export const TABS: { id: AdminTab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'ops', label: 'Ops Health' },
  { id: 'billing', label: 'Billing' },
  { id: 'users', label: 'Users' },
  { id: 'research', label: 'Research' },
  { id: 'email-logs', label: 'Email Logs' },
  { id: 'email-testing', label: 'Email Testing' },
]

export const VALID_TAB_IDS = TABS.map(t => t.id)

export function getTabFromHash(): AdminTab {
  const hash = window.location.hash.slice(1)
  return VALID_TAB_IDS.includes(hash as AdminTab) ? (hash as AdminTab) : 'dashboard'
}

interface AdminNavProps {
  activeTab: AdminTab
  onTabChange: (tab: AdminTab) => void
  user: User
  onSignOut: () => void
  stats?: {
    bookings: number
    active: number
    drops: number
    savings: number
  }
}

export function AdminNav({ activeTab, onTabChange, user, onSignOut, stats }: AdminNavProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header className="sticky top-0 z-40 bg-stone-50/90 dark:bg-stone-950/90 backdrop-blur-xl border-b border-stone-200/50 dark:border-stone-800/50">
      {/* Flight path arc — wrapped in its own overflow-hidden so it doesn't clip dropdown menus */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <path
            d="M-50,40 Q300,5 650,25 T1400,15"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 12"
            className="text-stone-300/20 dark:text-stone-600/15"
          />
        </svg>
      </div>

      {/* Row 1: Brand + Stats + User */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-3 pb-0 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CrownedBirdLogo className="w-9 h-9" />
          <span className="text-lg font-display font-medium text-stone-900 dark:text-stone-100">Perch Admin</span>
        </div>

        <div className="flex items-center gap-3">
          {stats && (
            <div className="hidden sm:flex items-center gap-6 text-sm">
              <div className="flex items-center gap-1.5">
                <span className="text-stone-400">Bookings</span>
                <span className="font-data font-semibold tabular-nums text-stone-900 dark:text-stone-100">{stats.bookings}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-stone-400">Active</span>
                <span className="font-data font-semibold tabular-nums text-stone-900 dark:text-stone-100">{stats.active}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-emerald-600 dark:text-emerald-400">Drops</span>
                <span className="font-data font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{stats.drops}</span>
              </div>
              {stats.savings > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-600 dark:text-emerald-400">Savings</span>
                  <span className="font-data font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">${stats.savings}</span>
                </div>
              )}
            </div>
          )}

          {/* Mobile hamburger */}
          <div className="relative md:hidden">
            <button
              onClick={() => setMobileMenuOpen(prev => !prev)}
              className="p-2 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg transition-colors"
              aria-label="Navigation menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {mobileMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMobileMenuOpen(false)} />
                <div
                  role="menu"
                  aria-label="Admin navigation"
                  className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 shadow-xl py-1 min-w-[200px]"
                >
                  {TABS.map(tab => (
                    <button
                      key={tab.id}
                      role="menuitem"
                      onClick={() => { onTabChange(tab.id); setMobileMenuOpen(false) }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        activeTab === tab.id
                          ? 'text-stone-900 dark:text-stone-100 font-medium bg-stone-50 dark:bg-stone-800'
                          : 'text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800/50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <UserMenu user={user} onSignOut={onSignOut} />
        </div>
      </div>

      {/* Row 2: Underline tabs (desktop only) */}
      <nav className="hidden md:block relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-end gap-0 -mb-px">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'text-stone-900 dark:text-stone-100'
                  : 'text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>
      </nav>
    </header>
  )
}
